import { initializeApp, getApps, FirebaseApp } from "firebase/app";
import { getAuth, Auth } from "firebase/auth";
import { getFirestore, Firestore } from "firebase/firestore";
import { getStorage, FirebaseStorage } from "firebase/storage";

// Firebase configuration
const firebaseConfig = {
  apiKey: process.env.NEXT_PUBLIC_FIREBASE_API_KEY,
  authDomain: process.env.NEXT_PUBLIC_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.NEXT_PUBLIC_FIREBASE_PROJECT_ID,
  storageBucket: process.env.NEXT_PUBLIC_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.NEXT_PUBLIC_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.NEXT_PUBLIC_FIREBASE_APP_ID,
  measurementId: process.env.NEXT_PUBLIC_FIREBASE_MEASUREMENT_ID,
};

// Guard flags — exported for use in hooks/contexts
export const isFirebaseConfigured =
  typeof firebaseConfig.apiKey === "string" && firebaseConfig.apiKey.length > 0;

export const isFirebaseDisabled =
  process.env.NEXT_PUBLIC_FIREBASE_BYPASS === "true";

// Lazy singleton — only initialise Firebase when actually needed (client-side + configured)
let _app: FirebaseApp | null = null;
let _auth: Auth | null = null;
let _db: Firestore | null = null;
let _storage: FirebaseStorage | null = null;

function ensureApp(): FirebaseApp {
  if (_app) return _app;
  if (typeof window === "undefined") throw new Error("Firebase is only available on the client");
  if (!isFirebaseConfigured) throw new Error("Firebase is not configured — set NEXT_PUBLIC_FIREBASE_* env vars");
  if (isFirebaseDisabled) throw new Error("Firebase is disabled via NEXT_PUBLIC_FIREBASE_BYPASS");
  _app = getApps().length === 0 ? initializeApp(firebaseConfig) : getApps()[0];
  return _app;
}

function getAppSafe(): FirebaseApp | null {
  try {
    return ensureApp();
  } catch {
    return null;
  }
}

// Lazy proxy for auth — accessing properties only initialises when Firebase is available
export const auth = new Proxy({} as Auth, {
  get(_, prop) {
    if (!_auth) {
      const app = getAppSafe();
      if (!app) return undefined;
      _auth = getAuth(app);
    }
    return (_auth as any)[prop];
  },
});

// Safe db getter — returns null when Firebase is not configured
export function getDb(): Firestore | null {
  if (_db) return _db;
  const app = getAppSafe();
  if (!app) return null;
  _db = getFirestore(app);
  return _db;
}

// Legacy export — only safe to use when isFirebaseConfigured is true
// Use getDb() for guarded access
export const db = new Proxy({} as Firestore, {
  get(_, prop) {
    const firestore = getDb();
    if (!firestore) return undefined;
    return (firestore as any)[prop];
  },
  getPrototypeOf() {
    const firestore = getDb();
    return firestore ? Object.getPrototypeOf(firestore) : Object.prototype;
  },
});

// Lazy proxy for storage
export const storage = new Proxy({} as FirebaseStorage, {
  get(_, prop) {
    if (!_storage) {
      const app = getAppSafe();
      if (!app) return undefined;
      _storage = getStorage(app);
    }
    return (_storage as any)[prop];
  },
});

// Analytics — only import dynamically when needed
export const getAnalyticsInstance = async () => {
  if (typeof window === "undefined" || !isFirebaseConfigured || isFirebaseDisabled) return null;
  try {
    const { getAnalytics } = await import("firebase/analytics");
    return getAnalytics(ensureApp());
  } catch {
    return null;
  }
};

export const app = getAppSafe() ?? undefined;
